import {
  ArrowPathIcon,
  LockClosedIcon,
  LockOpenIcon
} from '@heroicons/react/20/solid';
import { CpuChipIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import Tag from 'components/common/tag';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger
} from 'components/layout/popover';
import Tooltip from 'components/layout/tooltip';
import ProductLink from 'components/products/product-link';
import ProductPhoto from 'components/products/product-photo';
import {
  CondenserType,
  DuctedSystemProductType,
  SelectedDuctedSystemType,
  ValidDuctedSystemType
} from 'data/types';
import { useEffect, useState } from 'react';
import { abbreviateNumber, formatUSD, roundToDecimal } from 'utils/helpers';

type DuctedSystemMapProps = {
  condenser: CondenserType;
  system: ValidDuctedSystemType | SelectedDuctedSystemType;
  editable?: boolean;
  showTags?: boolean;
  showPrices?: boolean;
};

export default function DuctedSystemMap({
  condenser,
  system,
  editable = false,
  showTags = false,
  showPrices = false
}: DuctedSystemMapProps) {
  const [condenserInfo, setCondenserInfo] =
    useState<DuctedSystemProductType | null>(null);
  const [zoneInfo, setZoneInfo] = useState<DuctedSystemProductType[]>([]);
  const [condenserOptions, setCondenserOptions] = useState<
    DuctedSystemProductType[]
  >([]);
  const [zoneOptions, setZoneOptions] = useState<DuctedSystemProductType[][]>(
    []
  );
  const [condenserLocked, setCondenserLocked] = useState(false);
  const [lockedZones, setLockedZones] = useState<string[]>([]);

  useEffect(() => {
    if (system) {
      let condenserProduct = system.products.find(
        (p) => p.productType === 'Condenser Unit'
      );
      if (condenserProduct) {
        condenserProduct = {
          ...condenserProduct,
          specs: {
            seer: system.seer ?? undefined,
            seer2: system.seer2 ?? undefined,
            coolingCapacity:
              system.coolingCapacity2023 ??
              system.coolingCapacity2017 ??
              undefined
          }
        };
      }
      let furnaceProduct = system.products.find(
        (p) =>
          p.productType === 'Gas Furnace' || p.productType === 'Oil Furnace'
      );
      if (furnaceProduct) {
        furnaceProduct = {
          ...furnaceProduct,
          specs: {
            afue: system.afue ?? undefined,
            blowerType: system.blowerType ?? undefined
          }
        };
      }
      const coilProduct = system.products.find(
        (p) => p.productType === 'Evaporator Coil'
      );
      const newZoneInfo: DuctedSystemProductType[] = [];
      if (furnaceProduct) {
        newZoneInfo.push(furnaceProduct);
      }
      if (coilProduct) {
        newZoneInfo.push(coilProduct);
      }
      setCondenserInfo(condenserProduct ?? null);
      setZoneInfo(newZoneInfo);
    }
  }, [system]);

  useEffect(() => {
    const newCondenserOptions: DuctedSystemProductType[] = [];
    condenser.validDuctedSystems.forEach((s) => {
      let newCondenserOption = s.products.find((p) =>
        p.productType.toLowerCase().includes('condenser')
      );
      if (
        newCondenserOption &&
        newCondenserOptions.findIndex(
          (p) => p.productId === newCondenserOption?.productId
        ) === -1 &&
        newCondenserOption.productId !== condenserInfo?.productId
      ) {
        newCondenserOption = {
          ...newCondenserOption,
          specs: {
            seer: s.seer ?? undefined,
            seer2: s.seer2 ?? undefined,
            coolingCapacity:
              s.coolingCapacity2023 ?? s.coolingCapacity2017 ?? undefined
          }
        };
        newCondenserOptions.push(newCondenserOption);
      }
    });
    const newZoneOptions: DuctedSystemProductType[][] = [];
    const productTypes = zoneInfo.map((p) => p.productType);
    productTypes.forEach((productType) => {
      const newZoneOption: DuctedSystemProductType[] = [];
      condenser.validDuctedSystems.forEach((s) => {
        let newZoneOptionProduct = s.products.find((p) =>
          p.productType.includes(productType)
        );
        if (
          newZoneOptionProduct &&
          newZoneOption.findIndex(
            (p) => p.productId === newZoneOptionProduct?.productId
          ) === -1 &&
          zoneInfo.findIndex(
            (p) => p.productId === newZoneOptionProduct?.productId
          ) === -1
        ) {
          newZoneOptionProduct = {
            ...newZoneOptionProduct,
            specs: {
              afue: s.afue ?? undefined,
              blowerType: s.blowerType ?? undefined,
              cabinetSize: s.cabinetSize ?? undefined
            }
          };
          newZoneOption.push(newZoneOptionProduct);
        }
      });
      newZoneOptions.push(newZoneOption);
    });
    setCondenserOptions(newCondenserOptions);
    setZoneOptions(newZoneOptions);
  }, [condenser, condenserInfo, zoneInfo]);

  const handleCondenserClick = (product: DuctedSystemProductType) => {
    setCondenserInfo(product);
  };

  const handleZoneOptionClick = (
    productType: string,
    product: DuctedSystemProductType
  ) => {
    const productIndex = zoneInfo.findIndex((p) =>
      p.productType.includes(productType)
    );
    const newZoneInfo = [...zoneInfo];
    newZoneInfo[productIndex] = product;
    setZoneInfo(newZoneInfo);
  };

  const toggleZoneLock = (productType: string) => {
    if (lockedZones.includes(productType)) {
      setLockedZones((oldValue) =>
        oldValue.filter((zone) => zone !== productType)
      );
    } else {
      setLockedZones((oldValue) => [...oldValue, productType]);
    }
  };

  return (
    <div key={condenser.uniqueId} className='flex flex-col'>
      <div className='flex'>
        <div className='flex flex-col pr-2 pt-2'>
          <div className='h-3 w-3 rounded-full bg-gray-300' />
          <div className='ml-[4px] grow border-l-4 border-gray-300' />
        </div>
        <div className='flex grow flex-col gap-2'>
          <div className='flex flex-wrap items-center gap-2'>
            <CpuChipIcon className='h-6 w-6 text-gray-800' />
            <span className='text-xl font-semibold'>{condenser.name}</span>
            {editable && (
              <Popover placement='bottom-start'>
                <PopoverTrigger className='relative text-gray-400 hover:text-gray-500'>
                  <ArrowPathIcon className='h-6 w-6' />
                  <Tooltip text='Change Condenser' />
                </PopoverTrigger>
                <PopoverContent className='z-50 max-h-[500px] overflow-auto rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5'>
                  {condenserOptions.length > 0 ? (
                    <div className='table'>
                      <div className='table-row-group'>
                        {condenserOptions
                          .sort(
                            (a, b) =>
                              (a.specs?.seer ?? 0) - (b.specs?.seer ?? 0)
                          )
                          .sort(
                            (a, b) =>
                              (a.specs?.seer2 ?? 0) - (b.specs?.seer2 ?? 0)
                          )
                          .sort(
                            (a, b) =>
                              (a.specs?.coolingCapacity ?? 0) -
                              (b.specs?.coolingCapacity ?? 0)
                          )
                          .map((option) => (
                            <PopoverClose
                              className='group table-row rounded-md px-2 py-1 font-semibold'
                              onClick={() => handleCondenserClick(option)}
                              key={option.productId}
                            >
                              <div className='table-cell rounded-l-md p-1 align-middle group-hover:bg-gray-100'>
                                <ProductPhoto product={option} size='small' />
                              </div>
                              <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                <span className='whitespace-nowrap'>
                                  {option.model}
                                </span>
                              </div>
                              {option.productType === 'Condenser Unit' &&
                                option.specs?.coolingCapacity && (
                                  <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                    <Tag noWrap>
                                      <span>
                                        {abbreviateNumber(
                                          option.specs.coolingCapacity
                                        )}{' '}
                                        BTU
                                      </span>{' '}
                                      {condenserInfo?.specs?.coolingCapacity &&
                                        option.specs.coolingCapacity !==
                                          condenserInfo.specs.seer && (
                                          <span>
                                            (
                                            <span
                                              className={
                                                option.specs.coolingCapacity <
                                                condenserInfo.specs
                                                  .coolingCapacity
                                                  ? 'text-red-600'
                                                  : 'text-green-600'
                                              }
                                            >
                                              {option.specs.coolingCapacity <
                                              condenserInfo.specs
                                                .coolingCapacity
                                                ? '-'
                                                : '+'}
                                              {abbreviateNumber(
                                                Math.abs(
                                                  option.specs.coolingCapacity -
                                                    condenserInfo.specs
                                                      .coolingCapacity
                                                )
                                              )}
                                            </span>
                                            )
                                          </span>
                                        )}
                                    </Tag>
                                  </div>
                                )}
                              {option.productType === 'Condenser Unit' &&
                                option.specs?.seer &&
                                !option.specs.seer2 && (
                                  <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                    <Tag noWrap>
                                      <span>
                                        {roundToDecimal(option.specs.seer, 2)}{' '}
                                        SEER
                                      </span>{' '}
                                      {condenserInfo?.specs?.seer &&
                                        option.specs.seer !==
                                          condenserInfo.specs.seer && (
                                          <span>
                                            (
                                            <span
                                              className={
                                                option.specs.seer <
                                                condenserInfo.specs.seer
                                                  ? 'text-red-600'
                                                  : 'text-green-600'
                                              }
                                            >
                                              {option.specs.seer <
                                              condenserInfo.specs.seer
                                                ? '-'
                                                : '+'}
                                              {roundToDecimal(
                                                Math.abs(
                                                  option.specs.seer -
                                                    condenserInfo.specs.seer
                                                ),
                                                2
                                              )}
                                            </span>
                                            )
                                          </span>
                                        )}
                                    </Tag>
                                  </div>
                                )}
                              {option.productType === 'Condenser Unit' &&
                                option.specs?.seer2 && (
                                  <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                    <Tag noWrap>
                                      <span>
                                        {roundToDecimal(option.specs.seer2, 2)}{' '}
                                        SEER2
                                      </span>{' '}
                                      {condenserInfo?.specs?.seer2 &&
                                        option.specs.seer2 !==
                                          condenserInfo.specs.seer2 && (
                                          <span>
                                            (
                                            <span
                                              className={
                                                option.specs.seer2 <
                                                condenserInfo.specs.seer2
                                                  ? 'text-red-600'
                                                  : 'text-green-600'
                                              }
                                            >
                                              {option.specs.seer2 <
                                              condenserInfo.specs.seer2
                                                ? '-'
                                                : '+'}
                                              {roundToDecimal(
                                                Math.abs(
                                                  option.specs.seer2 -
                                                    condenserInfo.specs.seer2
                                                ),
                                                2
                                              )}
                                            </span>
                                            )
                                          </span>
                                        )}
                                    </Tag>
                                  </div>
                                )}
                              <div className='table-cell rounded-r-md p-1 align-middle group-hover:bg-gray-100'>
                                <span
                                  className={`text-sm font-semibold ${
                                    option.retailPrice <=
                                    (condenserInfo?.retailPrice ?? 0)
                                      ? 'text-green-600'
                                      : 'text-red-600'
                                  }`}
                                >
                                  {option.retailPrice <
                                  (condenserInfo?.retailPrice ?? 0)
                                    ? '-'
                                    : '+'}
                                  {formatUSD(
                                    Math.abs(
                                      option.retailPrice -
                                        (condenserInfo?.retailPrice ?? 0)
                                    )
                                  )}
                                </span>
                              </div>
                            </PopoverClose>
                          ))}
                      </div>
                    </div>
                  ) : (
                    <div className='max-w-[400px]'>
                      No other condensers available.
                      {lockedZones.length > 0 &&
                        ' Try unlocking other products to see more options.'}
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            )}
          </div>
          <div className='flex grow items-start gap-3'>
            <ProductPhoto size='medium' product={condenserInfo ?? undefined} />
            <div className='flex grow flex-col gap-1'>
              <div className='flex w-full items-center'>
                <div className='flex grow flex-wrap items-center gap-2'>
                  <span className='text-lg font-semibold'>
                    {condenserInfo?.name ?? 'Unknown Product'}
                  </span>
                  {showTags && (
                    <>
                      <Tag
                        text={`${abbreviateNumber(
                          Math.round(
                            (system?.coolingCapacity2023 ?? 0) / 1000
                          ) * 1000
                        )}`}
                      />
                      <Tag text={`${system?.seer} SEER`} />
                    </>
                  )}
                </div>
                {showPrices && condenserInfo && (
                  <span className='text-right text-lg font-bold'>
                    {formatUSD(
                      condenserInfo.retailPrice * condenserInfo.quantity,
                      true
                    )}
                  </span>
                )}
              </div>
              {condenserInfo && (
                <ProductLink
                  type='ducted product'
                  product={condenserInfo}
                  quantity={condenserInfo.quantity}
                />
              )}
            </div>
            {editable && (
              <button
                type='button'
                className='relative p-2 text-gray-300 hover:text-gray-400'
                onClick={() => setCondenserLocked((oldValue) => !oldValue)}
              >
                {condenserLocked ? (
                  <LockClosedIcon className='h-6 w-6 text-gray-400' />
                ) : (
                  <LockOpenIcon className='h-6 w-6' />
                )}
                <Tooltip
                  text={condenserLocked ? 'Unlock Condenser' : 'Lock Condenser'}
                />
              </button>
            )}
          </div>
        </div>
      </div>
      {zoneInfo.map((product, productIndex) => (
        <div key={product.productId} className='flex items-start gap-2'>
          <div className='relative flex items-start self-stretch'>
            {productIndex < zoneInfo.length - 1 && (
              <div className='absolute left-[4px] h-full border-l-4 border-gray-300' />
            )}
            <div className='mb-[4px] ml-[4px] w-7 rounded-bl-2xl border-b-4 border-l-4 border-gray-300 pt-10' />
            <div className='mt-[36px] h-3 w-3 rounded-full bg-gray-300' />
          </div>
          <div className='flex grow flex-col gap-2 pt-7'>
            <div className='flex flex-wrap items-center gap-2'>
              <RectangleStackIcon className='h-6 w-6 shrink-0 text-gray-800' />
              <span className='shrink-0 text-xl font-semibold'>
                {product.productType}
              </span>
              {editable && (
                <Popover placement='bottom-start'>
                  <PopoverTrigger className='relative text-gray-400 hover:text-gray-500'>
                    <ArrowPathIcon className='h-6 w-6' />
                    <Tooltip text={`Change ${product.productType}`} />
                  </PopoverTrigger>
                  <PopoverContent className='z-50 max-h-[500px] overflow-auto rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5'>
                    {zoneOptions[productIndex]?.length > 0 ? (
                      <div className='table'>
                        <div className='table-row-group'>
                          {zoneOptions[productIndex]
                            .sort(
                              (a, b) =>
                                (a.specs?.afue ?? 0) - (b.specs?.afue ?? 0)
                            )
                            .sort((a, b) =>
                              (a.specs?.blowerType ?? '').localeCompare(
                                b.specs?.blowerType ?? ''
                              )
                            )
                            .sort((a, b) =>
                              (a.specs?.cabinetSize ?? '').localeCompare(
                                b.specs?.cabinetSize ?? ''
                              )
                            )
                            .map((option) => (
                              <PopoverClose
                                key={option.productId}
                                className='group table-row rounded-md px-2 py-1 font-semibold'
                                onClick={() =>
                                  handleZoneOptionClick(
                                    option.productType,
                                    option
                                  )
                                }
                              >
                                <div className='table-cell rounded-l-md p-1 align-middle group-hover:bg-gray-100'>
                                  <ProductPhoto product={option} size='small' />
                                </div>
                                <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                  <span className='whitespace-nowrap'>
                                    {option.model}
                                  </span>
                                </div>
                                {(option.productType === 'Gas Furnace' ||
                                  option.productType === 'Oil Furnace') &&
                                  option.specs?.blowerType && (
                                    <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                      <Tag noWrap>
                                        {option.specs.blowerType} Blower
                                      </Tag>
                                    </div>
                                  )}
                                {(option.productType === 'Gas Furnace' ||
                                  option.productType === 'Oil Furnace') &&
                                  option.specs?.afue && (
                                    <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                      <Tag noWrap>
                                        <span>
                                          {abbreviateNumber(option.specs.afue)}%
                                          AFUE
                                        </span>{' '}
                                        {product.specs?.afue &&
                                          option.specs.afue !==
                                            product.specs.afue && (
                                            <span>
                                              (
                                              <span
                                                className={
                                                  option.specs.afue <
                                                  product.specs.afue
                                                    ? 'text-red-600'
                                                    : 'text-green-600'
                                                }
                                              >
                                                {option.specs.afue <
                                                product.specs.afue
                                                  ? '-'
                                                  : '+'}
                                                {abbreviateNumber(
                                                  Math.abs(
                                                    option.specs.afue -
                                                      product.specs.afue
                                                  )
                                                )}
                                                %
                                              </span>
                                              )
                                            </span>
                                          )}
                                      </Tag>
                                    </div>
                                  )}
                                {option.productType === 'Evaporator Coil' &&
                                  option.specs?.cabinetSize && (
                                    <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                      <Tag noWrap>
                                        Size {option.specs.cabinetSize}
                                      </Tag>
                                    </div>
                                  )}
                                <div className='table-cell rounded-r-md p-1 align-middle group-hover:bg-gray-100'>
                                  <span
                                    className={`text-sm font-semibold ${
                                      option.retailPrice <=
                                      zoneInfo[productIndex].retailPrice
                                        ? 'text-green-600'
                                        : 'text-red-600'
                                    }`}
                                  >
                                    {option.retailPrice <
                                    zoneInfo[productIndex].retailPrice
                                      ? '-'
                                      : '+'}
                                    {formatUSD(
                                      Math.abs(
                                        option.retailPrice -
                                          zoneInfo[productIndex].retailPrice
                                      )
                                    )}
                                  </span>
                                </div>
                              </PopoverClose>
                            ))}
                        </div>
                      </div>
                    ) : (
                      <div className='max-w-[400px]'>
                        No other products available.
                        {(condenserLocked || lockedZones.length > 0) &&
                          ' Try unlocking condenser or other zones to see more options.'}
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <div className='flex grow items-start gap-3'>
              <ProductPhoto size='medium' product={product} />
              <div className='flex grow flex-col gap-1'>
                <div className='flex w-full items-center'>
                  <div className='flex grow flex-wrap items-center gap-2'>
                    <span className='text-lg font-semibold'>
                      {product.name}
                    </span>
                  </div>
                  {showPrices && (
                    <span className='text-right text-lg font-bold'>
                      {formatUSD(product.retailPrice * product.quantity, true)}
                    </span>
                  )}
                </div>
                <ProductLink
                  type='ducted product'
                  product={product}
                  quantity={product.quantity}
                />
              </div>
              {editable && (
                <button
                  type='button'
                  className='relative p-2 text-gray-300 hover:text-gray-400'
                  onClick={() => toggleZoneLock(product.productType)}
                >
                  {lockedZones.includes(product.productType) ? (
                    <LockClosedIcon className='h-6 w-6 text-gray-400' />
                  ) : (
                    <LockOpenIcon className='h-6 w-6' />
                  )}
                  <Tooltip
                    text={
                      lockedZones.includes(product.productType)
                        ? `Unlock ${product.productType}`
                        : `Lock ${product.productType}`
                    }
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
